import { useEffect, useRef } from "react";
import { useSubscription } from "@/contexts/SubscriptionContext";

const PUBLISHER_ID = "pub-7569308823196579";

// Load the AdSense script once globally
let adsenseLoaded = false;
const loadAdSenseScript = () => {
  if (adsenseLoaded) return;
  adsenseLoaded = true;
  const script = document.createElement("script");
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-${PUBLISHER_ID}`;
  script.async = true;
  script.crossOrigin = "anonymous";
  document.head.appendChild(script);
};

/**
 * Google AdSense display ad component.
 * @param {string} slot - Ad unit slot ID (optional, for manual units)
 * @param {"horizontal"|"vertical"|"rectangle"|"auto"} format - Ad format
 * @param {boolean} responsive - Whether to use responsive sizing
 * @param {string} className - Additional CSS classes
 */
export default function AdSense({
  slot,
  format = "auto",
  responsive = true,
  className = "",
}) {
  const adRef = useRef(null);
  const pushed = useRef(false);

  useEffect(() => {
    loadAdSenseScript();
  }, []);

  useEffect(() => {
    if (pushed.current) return;
    try {
      if (window.adsbygoogle && adRef.current) {
        window.adsbygoogle.push({});
        pushed.current = true;
      }
    } catch (e) {
      // AdSense may not be ready yet or ad blocker active
    }
  }, []);

  const formatStyles = {
    horizontal: { display: "block", minHeight: "90px" },
    vertical: { display: "block", width: "160px", minHeight: "600px" },
    rectangle: { display: "inline-block", width: "300px", height: "250px" },
    auto: { display: "block" },
  };

  return (
    <div
      className={`adsense-container ${className}`}
      data-testid="adsense-ad"
    >
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={formatStyles[format] || formatStyles.auto}
        data-ad-client={`ca-${PUBLISHER_ID}`}
        {...(slot ? { "data-ad-slot": slot } : {})}
        {...(responsive ? { "data-full-width-responsive": "true" } : {})}
        {...(format === "auto" ? { "data-ad-format": "auto" } : {})}
      />
    </div>
  );
}

/**
 * Horizontal banner ad — suitable for top/bottom of page content
 */
export function AdBanner({ className = "" }) {
  let isPro = false;
  let paywallEnabled = false;
  try {
    const sub = useSubscription();
    isPro = sub.isPro;
    paywallEnabled = sub.paywallEnabled;
  } catch {}
  // Hide ads for Pro users when paywall is enabled
  if (paywallEnabled && isPro) return null;
  return (
    <AdSense
      format="horizontal"
      className={`w-full my-4 ${className}`}
    />
  );
}

/**
 * Rectangle ad — suitable for sidebar or between content sections
 */
export function AdRectangle({ className = "" }) {
  return (
    <AdSense
      format="rectangle"
      className={`flex justify-center my-4 ${className}`}
    />
  );
}
